import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlusCircle, FiEye, FiEyeOff } from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminBlogs = ({ onNavigateToEditor }) => {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState('');

    const token = localStorage.getItem('adminToken');

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const res = await fetch(`${API_URL}/blogs?all=true&limit=100`);
            const data = await res.json();
            if (data.success) setPosts(data.data);
            else setError('Failed to load posts');
        } catch (err) {
            setError('Could not connect to blog API');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPosts(); }, []);

    const handleTogglePublish = async (post) => {
        setActionLoading(post._id + '_toggle');
        try {
            const res = await fetch(`${API_URL}/blogs/${post._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ published: !post.published })
            });
            const data = await res.json();
            if (data.success) {
                setPosts(prev => prev.map(p => p._id === post._id ? data.data : p));
            } else {
                alert(data.message || 'Failed to update');
            }
        } catch (err) {
            alert('Network error');
        } finally {
            setActionLoading('');
        }
    };

    const handleDelete = async (post) => {
        if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
        setActionLoading(post._id + '_delete');
        try {
            const res = await fetch(`${API_URL}/blogs/${post._id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setPosts(prev => prev.filter(p => p._id !== post._id));
            } else {
                alert(data.message || 'Failed to delete');
            }
        } catch (err) {
            alert('Network error');
        } finally {
            setActionLoading('');
        }
    };

    const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

    const style = {
        container: { padding: '0.5rem 0' },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
        title: { fontSize: '1.4rem', fontWeight: 700, color: 'var(--text)' },
        newBtn: {
            display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px',
            background: 'var(--primary)', color: '#fff', border: 'none',
            borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem'
        },
        table: { width: '100%', borderCollapse: 'collapse' },
        th: {
            padding: '12px 16px', textAlign: 'left', color: 'var(--text-muted)',
            fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase',
            borderBottom: '1px solid var(--border)', letterSpacing: '0.05em'
        },
        td: { padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)', verticalAlign: 'middle' },
        postTitle: { color: 'var(--text)', fontWeight: 600, fontSize: '0.95rem' },
        postExcerpt: { color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: '2px', maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
        badge: (pub) => ({
            display: 'inline-block', padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600,
            background: pub ? 'rgba(16, 185, 129, 0.15)' : 'rgba(100, 116, 139, 0.15)',
            color: pub ? '#10b981' : '#64748b'
        }),
        catBadge: {
            display: 'inline-block', padding: '3px 10px', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600,
            background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8'
        },
        iconBtn: (color) => ({
            background: 'none', border: 'none', cursor: 'pointer', color, padding: '6px', borderRadius: '6px',
            transition: 'background 0.15s', display: 'inline-flex', alignItems: 'center'
        }),
        actionsCell: { display: 'flex', gap: '4px', alignItems: 'center' },
        emptyState: { textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }
    };

    return (
        <div style={style.container}>
            <div style={style.header}>
                <span style={style.title}>📝 Blog Management</span>
                <button style={style.newBtn} onClick={() => onNavigateToEditor(null)}>
                    <FiPlusCircle /> New Post
                </button>
            </div>

            {loading ? (
                <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
            ) : error ? (
                <p style={{ color: 'var(--danger)' }}>{error}</p>
            ) : posts.length === 0 ? (
                <div style={style.emptyState}>
                    <p style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📄</p>
                    <p>No blog posts yet. Create your first post!</p>
                    <button style={{ ...style.newBtn, margin: '1rem auto' }} onClick={() => onNavigateToEditor(null)}>
                        <FiPlusCircle /> Create First Post
                    </button>
                </div>
            ) : (
                <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
                    <table style={style.table}>
                        <thead>
                            <tr>
                                <th style={style.th}>Title</th>
                                <th style={style.th}>Category</th>
                                <th style={style.th}>Status</th>
                                <th style={style.th}>Views</th>
                                <th style={style.th}>Date</th>
                                <th style={style.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {posts.map(post => (
                                <tr key={post._id} style={{ transition: 'background 0.15s' }}>
                                    <td style={style.td}>
                                        <div style={style.postTitle}>{post.title}</div>
                                        <div style={style.postExcerpt}>{post.excerpt}</div>
                                    </td>
                                    <td style={style.td}><span style={style.catBadge}>{post.category}</span></td>
                                    <td style={style.td}><span style={style.badge(post.published)}>{post.published ? 'Published' : 'Draft'}</span></td>
                                    <td style={{ ...style.td, color: 'var(--text-muted)' }}>{post.views}</td>
                                    <td style={{ ...style.td, color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                                        {formatDate(post.publishedAt || post.createdAt)}
                                    </td>
                                    <td style={style.td}>
                                        <div style={style.actionsCell}>
                                            <button
                                                style={style.iconBtn(post.published ? '#64748b' : '#10b981')}
                                                onClick={() => handleTogglePublish(post)}
                                                disabled={actionLoading === post._id + '_toggle'}
                                                title={post.published ? 'Unpublish' : 'Publish'}
                                            >
                                                {post.published ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                            </button>
                                            <button
                                                style={style.iconBtn('#818cf8')}
                                                onClick={() => onNavigateToEditor(post._id)}
                                                title="Edit"
                                            >
                                                <FiEdit2 size={16} />
                                            </button>
                                            <button
                                                style={style.iconBtn('#ef4444')}
                                                onClick={() => handleDelete(post)}
                                                disabled={actionLoading === post._id + '_delete'}
                                                title="Delete"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminBlogs;
